import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Loader2, Pencil, Plus, Search, ShieldCheck, Trash2, UserPlus, XCircle } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { useToast } from '../../hooks/use-toast';
import { adminApi } from '../../context/AdminContext';

const emptyForm = {
  username: '',
  password: '',
  is_active: true,
};

const AdminStaff = () => {
  const { toast } = useToast();
  const [staffUsers, setStaffUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  const loadStaffUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminApi.get('/staff-users');
      setStaffUsers(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load staff accounts', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { loadStaffUsers(); }, [loadStaffUsers]);

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return staffUsers;
    return staffUsers.filter((staff) => staff.username?.toLowerCase().includes(q));
  }, [query, staffUsers]);

  const activeCount = staffUsers.filter((staff) => staff.is_active !== false).length;

  const resetForm = () => {
    setEditingUser(null);
    setFormData(emptyForm);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (staff) => {
    setEditingUser(staff);
    setFormData({
      username: staff.username || '',
      password: '',
      is_active: staff.is_active !== false,
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    resetForm();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = {
        username: formData.username.trim(),
        is_active: formData.is_active,
      };
      if (!editingUser || formData.password) {
        payload.password = formData.password;
      }
      if (editingUser) {
        await adminApi.put(`/staff-users/${editingUser.id}`, payload);
        toast({ title: 'Updated', description: 'Staff account updated' });
      } else {
        await adminApi.post('/staff-users', payload);
        toast({ title: 'Created', description: 'Staff account created' });
      }
      closeDialog();
      await loadStaffUsers();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save staff account',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (staff) => {
    if (!window.confirm(`Delete staff account "${staff.username}"?`)) return;
    try {
      await adminApi.delete(`/staff-users/${staff.id}`);
      toast({ title: 'Deleted', description: 'Staff account deleted' });
      await loadStaffUsers();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete staff account', variant: 'destructive' });
    }
  };

  return (
    <div data-testid="admin-staff-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl sm:text-3xl font-bold text-gray-900">Staff Accounts</h2>
          <p className="text-gray-600 mt-1">Capture-only Android access</p>
        </div>
        <Button className="bg-red-600 hover:bg-red-700" onClick={openCreateDialog} data-testid="add-staff-btn">
          <UserPlus className="mr-2 h-4 w-4" />
          Add Staff
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-500">Total Staff</div>
            <div className="text-2xl font-bold text-gray-900">{staffUsers.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-500">Active</div>
            <div className="text-2xl font-bold text-green-600">{activeCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-500">Disabled</div>
            <div className="text-2xl font-bold text-red-600">{staffUsers.length - activeCount}</div>
          </CardContent>
        </Card>
      </div>

      <div className="mb-4 max-w-md relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search staff accounts"
          className="pl-10"
          data-testid="staff-search"
        />
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <ShieldCheck className="h-16 w-16 mx-auto mb-4 text-gray-300" />
          <p className="text-lg">{staffUsers.length === 0 ? 'No staff accounts yet' : 'No staff accounts match your search'}</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border overflow-hidden">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow>
                <TableHead className="p-3">Staff</TableHead>
                <TableHead className="p-3">Permission</TableHead>
                <TableHead className="p-3">Status</TableHead>
                <TableHead className="p-3">Created</TableHead>
                <TableHead className="p-3 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((staff) => (
                <TableRow key={staff.id} data-testid={`staff-row-${staff.id}`}>
                  <TableCell className="p-3">
                    <div className="font-semibold text-gray-900">{staff.username}</div>
                    <div className="text-xs text-gray-500 font-mono">{staff.id}</div>
                  </TableCell>
                  <TableCell className="p-3">
                    <Badge className="bg-blue-100 text-blue-700">
                      <ShieldCheck className="mr-1 h-3 w-3" />
                      Capture only
                    </Badge>
                  </TableCell>
                  <TableCell className="p-3">
                    {staff.is_active === false ? (
                      <Badge className="bg-red-100 text-red-700">
                        <XCircle className="mr-1 h-3 w-3" />
                        Disabled
                      </Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-700">
                        <CheckCircle2 className="mr-1 h-3 w-3" />
                        Active
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="p-3 text-gray-600">
                    {staff.created_at ? new Date(staff.created_at).toLocaleString() : 'N/A'}
                  </TableCell>
                  <TableCell className="p-3">
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" size="sm" onClick={() => openEditDialog(staff)} aria-label={`Edit ${staff.username}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="destructive" size="sm" onClick={() => handleDelete(staff)} aria-label={`Delete ${staff.username}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={(open) => (open ? setDialogOpen(true) : closeDialog())}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Edit Staff Account' : 'Add Staff Account'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="staff-username">Username</Label>
              <Input
                id="staff-username"
                value={formData.username}
                onChange={(event) => setFormData({ ...formData, username: event.target.value })}
                autoComplete="username"
                required
                data-testid="staff-username"
              />
            </div>
            <div>
              <Label htmlFor="staff-password">{editingUser ? 'New Password' : 'Password'}</Label>
              <Input
                id="staff-password"
                type="password"
                value={formData.password}
                onChange={(event) => setFormData({ ...formData, password: event.target.value })}
                autoComplete={editingUser ? 'new-password' : 'new-password'}
                required={!editingUser}
                minLength={6}
                data-testid="staff-password"
              />
            </div>
            <div className="flex items-center justify-between rounded-md border p-3">
              <Label htmlFor="staff-active" className="font-medium">Active account</Label>
              <Switch
                id="staff-active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeDialog} disabled={saving}>Cancel</Button>
              <Button type="submit" className="bg-red-600 hover:bg-red-700" disabled={saving}>
                {saving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : editingUser ? (
                  <Pencil className="mr-2 h-4 w-4" />
                ) : (
                  <Plus className="mr-2 h-4 w-4" />
                )}
                {editingUser ? 'Save Changes' : 'Create Account'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminStaff;
